import { AppLayout } from '@/components/layouts/AppLayout'
import { DataTableRow } from '@/components/molecules/DataTableRow'
import { ListSearchInput } from '@/components/molecules/ListFilters'
import { ListPagination } from '@/components/molecules/ListPagination'
import { SortableTableHead } from '@/components/molecules/SortableTableHead'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useCabasEstimates } from '@/hooks/api/useCabasEstimates'
import { useListParams } from '@/hooks/useListParams'
import type { CabasEstimateListQuery } from '@backend/schemas'
import { format } from 'date-fns'
import { useNavigate } from 'react-router-dom'

type SortField = CabasEstimateListQuery['sortField']

const CabasSync = () => {
  const navigate = useNavigate()
  const { params, update, setSort } = useListParams<SortField>({ sortField: 'updatedAt', sortDir: 'desc' })
  const { workshop: _workshop, ...query } = params
  const { data: cabasData, isLoading } = useCabasEstimates(query)

  const sortProps = {
    sortField: params.sortField,
    sortDir: params.sortDir,
    onSort: (field: SortField) => setSort(field, field === 'Damage.DamageDate' || field === 'updatedAt' ? 'desc' : 'asc'),
    disabled: !!params.search,
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Cabas Sync</CardTitle>
            <div className="flex gap-4 mt-4">
              <ListSearchInput
                placeholder="Sök Regnr eller Calculation ID (börjar med)..."
                value={params.search}
                onChange={search => update({ search })}
              />
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Calculation ID</TableHead>
                      <SortableTableHead label="Regnr" field="Vehicle.VehicleIdentification" {...sortProps} />
                      <SortableTableHead label="Verkstad" field="BodyWorkshop.ContactInfo.Name" {...sortProps} />
                      <SortableTableHead label="Skadedatum" field="Damage.DamageDate" {...sortProps} />
                      <SortableTableHead label="Senast uppdaterad" field="updatedAt" {...sortProps} />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cabasData?.items.length ? (
                      cabasData.items.map(estimate => (
                        <DataTableRow key={estimate.id} onClick={() => navigate(`/cabas-sync/${estimate.id}`)}>
                          <TableCell className="font-medium">{estimate.calculationId}</TableCell>
                          <TableCell>{estimate.registrationNumber}</TableCell>
                          <TableCell>{estimate.workshop}</TableCell>
                          <TableCell>
                            {estimate.damageDate ? format(new Date(estimate.damageDate), 'yyyy-MM-dd') : '-'}
                          </TableCell>
                          <TableCell>{format(new Date(estimate.lastUpdate), 'yyyy-MM-dd HH:mm')}</TableCell>
                        </DataTableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                          {params.search ? 'Inga kalkyler matchade sökningen' : 'Ingen data kunde hittas från cabas sync'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                <ListPagination
                  page={params.page}
                  pageSize={cabasData?.pageSize ?? 100}
                  total={cabasData?.total ?? 0}
                  itemLabel="kalkyler"
                  onPageChange={page => update({ page: String(page) })}
                />
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}

export default CabasSync
